import { memo, useState, useEffect } from "react";
import { Save, Trash2, Loader2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import type { FilterState } from "@/types/dashboard";

interface FiltersHeaderProps {
  isCounting: boolean;
  filteredCount: number | null;
  totalCount: number | null;
  presetsOpen: boolean;
  onPresetsOpenChange: (open: boolean) => void;
  savedPresets: Record<string, FilterState>;
  onSavePreset: (name: string) => void;
  onLoadPreset: (name: string) => void;
  onDeletePreset: (name: string, e: React.MouseEvent) => void;
}

export const FiltersHeader = memo(
  ({
    isCounting,
    filteredCount,
    totalCount,
    presetsOpen,
    onPresetsOpenChange,
    savedPresets,
    onSavePreset,
    onLoadPreset,
    onDeletePreset,
  }: FiltersHeaderProps) => {
    const [isNaming, setIsNaming] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");

    // Reset naming state when popover closes
    useEffect(() => {
      if (!presetsOpen) {
        setIsNaming(false);
        setNewPresetName("");
      }
    }, [presetsOpen]);

    const handleSaveClick = (e: React.MouseEvent | React.FormEvent) => {
      e.preventDefault();
      if (newPresetName.trim()) {
        onSavePreset(newPresetName);
      }
    };

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[8px]">
          <span className="text-sm font-semibold text-foreground">Filters</span>
          <div className="flex items-center gap-1 rounded-[18px] bg-primary px-1.5 py-0.5 text-xs font-medium text-primary-foreground">
            {isCounting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <span>Found: {filteredCount ?? "-"}</span>
            )}
          </div>
          <span className="text-sm text-muted-foreground">/ {totalCount}</span>
        </div>

        <Popover open={presetsOpen} onOpenChange={onPresetsOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer rounded-[18px] border border-border px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              <svg
                width="11"
                height="14"
                viewBox="0 0 11 14"
                fill="none"
                className="mr-2"
              >
                <path
                  d="M10.0003 12.6666L5.33366 9.99996L0.666992 12.6666V1.99996C0.666992 1.64634 0.807468 1.3072 1.05752 1.05715C1.30756 0.807102 1.6467 0.666626 2.00033 0.666626H8.66699C9.02061 0.666626 9.35975 0.807102 9.6098 1.05715C9.85985 1.3072 10.0003 1.64634 10.0003 1.99996V12.6666Z"
                  stroke="currentColor"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Presets
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[240px] p-0 overflow-hidden">
            {isNaming ? (
              <div className="p-3 space-y-3">
                <div className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
                  Name your preset
                </div>
                <form onSubmit={handleSaveClick} className="space-y-3">
                  <Input
                    autoFocus
                    placeholder="e.g. My view"
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-8 text-xs rounded-md"
                      onClick={() => setIsNaming(false)}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      className="h-8 flex-1 rounded-md bg-primary text-xs text-primary-foreground hover:bg-primary/90"
                      disabled={!newPresetName.trim()}
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <Command>
                <CommandList>
                  <CommandGroup heading="Actions">
                    <CommandItem
                      onSelect={() => setIsNaming(true)}
                      className="cursor-pointer"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save current view
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup heading="Saved Presets">
                    {Object.keys(savedPresets).length === 0 ? (
                      <div className="py-2 px-2 text-xs text-muted-foreground text-center">
                        No presets saved
                      </div>
                    ) : (
                      Object.keys(savedPresets).map((name) => (
                        <CommandItem
                          key={name}
                          className="flex justify-between items-center group p-0"
                        >
                          <div
                            className="flex-1 px-2 py-1.5 cursor-pointer"
                            onClick={() => onLoadPreset(name)}
                          >
                            {name}
                          </div>
                          <button
                            type="button"
                            className="cursor-pointer px-2 py-1.5 text-muted-foreground transition-colors hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onDeletePreset(name, e);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        </CommandItem>
                      ))
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

FiltersHeader.displayName = "FiltersHeader";
